import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../../styles/zLayers';
import { positionDropdown, dropdownWrapperStyle, DropdownPosition } from '../../../utils/dropdownPosition';

/**
 * Portal host for the short horizontal control strips that hang off overlay
 * toolbar buttons (background/gradient, border). The strips used to be
 * absolutely positioned inside the media box, where any ancestor
 * overflow-hidden clipped them at the card edge; rendering through a portal
 * with the shared positionDropdown clamp keeps them fully visible no matter
 * where their anchor button sits.
 *
 * Width is measured from the rendered strip (never hardcoded) — the first
 * paint happens hidden, positioned before the browser shows it.
 */
interface AnchoredStripPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  /** Suppress outside-click closing while a nested portal (color picker) is open. */
  suspendOutsideClose?: boolean;
  children: React.ReactNode;
}

const STRIP_ESTIMATED_HEIGHT = 44;
export const STRIP_Z_INDEX = Z.STRIP;

const AnchoredStripPopover: React.FC<AnchoredStripPopoverProps> = ({
  anchorRef,
  onClose,
  suspendOutsideClose = false,
  children,
}) => {
  const stripRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<DropdownPosition | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!anchor) return;
      const width = stripRef.current?.offsetWidth || 240;
      setPos(positionDropdown(anchor, width, STRIP_ESTIMATED_HEIGHT, { align: 'center' }));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (suspendOutsideClose) return;
      const target = e.target as Node;
      if (stripRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, anchorRef, suspendOutsideClose]);

  return createPortal(
    // Above the tended item modal (999999) and the template editor modal
    // (1000003) the strip can open over, below the max-z color picker that
    // nests inside it.
    <div style={pos ? dropdownWrapperStyle(pos, STRIP_Z_INDEX) : { position: 'fixed', visibility: 'hidden' }}>
      <div
        ref={stripRef}
        className="bg-black/60 backdrop-blur-sm rounded px-2 py-1 flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export default AnchoredStripPopover;
